import type { CropRect } from './types'

/**
 * Il valore di un filtro sui metadati.
 *
 * Una stringa è un valore esatto, una lista è "uno qualunque di questi" (più
 * caselle spuntate), una coppia è un intervallo per i campi numerici.
 */
export type MetaFilterValue = string | readonly string[] | { from?: number; to?: number }

export interface CatalogFilters {
  q?: string
  /** Id dell'elemento di riferimento, per la ricerca "simile a". */
  similarTo?: string
  /** Tipo di contenuto; assente significa quello predefinito dell'azienda. */
  type?: string
  /**
   * Filtri sui metadati, con le chiavi dichiarate nel registro dell'azienda.
   * Nessun nome di campo è noto qui: arrivano tutti da GET /catalog/schema.
   *
   * ATTENZIONE alla stabilità del riferimento: questo oggetto finisce nella
   * chiave di cache, e ricrearlo a ogni render la invaliderebbe ogni volta.
   * Vedi EMPTY_FILTERS in useCatalog.ts — stesso motivo per cui esiste
   * EMPTY_CROP.
   */
  meta?: Readonly<Record<string, MetaFilterValue>>
}

/**
 * Chiavi di cache in un posto solo.
 *
 * Sparse fra i componenti diventano stringhe magiche che nessuno osa più
 * invalidare; qui si legge a colpo d'occhio cosa invalida cosa.
 */
export const queryKeys = {
  catalog: {
    all: ['catalog'] as const,
    /** Il registro dei metadati dell'azienda: cambia quasi mai. */
    schema: ['catalog', 'schema'] as const,
    search: (filters: CatalogFilters) => ['catalog', 'search', filters] as const,
    /**
     * L'immagine entra nella chiave come identificativo locale, non come
     * sorgente: una `data:` URL da mezzo megabyte dentro una chiave di cache
     * la renderebbe impossibile da confrontare a ogni render.
     */
    byImage: (referenceId: string, crop: CropRect) =>
      ['catalog', 'by-image', referenceId, crop] as const,
  },
  searchHistory: ['search-history'] as const,
  collections: {
    all: ['collections'] as const,
  },
  presentations: {
    documents: ['presentations', 'documents'] as const,
    experiences: ['presentations', 'experiences'] as const,
  },
  entitlements: ['entitlements'] as const,
  credits: ['credits'] as const,
  activity: ['activity'] as const,
  recommendations: ['recommendations'] as const,
  me: ['me'] as const,
  companies: ['companies'] as const,
} as const
